import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, IconButton, LinearProgress, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper,
  Stack, Switch, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { addDays, amount, today, weekStart, weekTitle } from '../labels';
import { SectionTitle, useNotify } from '../common';
import { applyRequest } from './mealPlanShopping';

function changeText(c: T.ShoppingChange): string {
  switch (c.action) {
    case 'Add': return `${c.name} ${amount(c.newQuantity, c.newUnit, c.hasUnquantified)}`.trim();
    case 'Remove': return `${c.name} (já não é preciso)`;
    default: return `${c.name}: ${amount(c.currentQuantity, c.currentUnit, false)} → ${amount(c.newQuantity, c.newUnit, c.hasUnquantified)}`;
  }
}

/**
 * Brings the "to buy" list in line with a week's meal plan: what would be added, changed or removed,
 * and which pantry staples are missing. Items to add can be unticked (they are at home): they stay out of the
 * week's list until ticked again under "Deixaste de fora". Changes and removals can be unticked to keep the line as
 * it is. Nothing changes until "Atualizar a lista".
 */
const MealPlanShoppingPage: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [params, setParams] = useSearchParams();
  const week = weekStart(params.get('semana') || today());
  const [preview, setPreview] = useState<T.MealPlanShoppingPreview | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const [unticked, setUnticked] = useState<string[]>([]);
  const [restored, setRestored] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    setError(null);
    setPreview(null);
    setMissing([]);
    setUnticked([]);
    setRestored([]);
    api.previewMealPlanShopping(week)
      .then(p => { if (alive) setPreview(p); })
      .catch((e: any) => { if (alive) setError(e.message ?? 'Ocorreu um erro.'); })
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [week]);

  const moveWeek = (days: number) => setParams({ semana: addDays(week, days) }, { replace: true });
  const flip = (id: string, on: boolean) => (ids: string[]) => (on ? ids.concat(id) : ids.filter(x => x !== id));
  const toggle = (id: string, on: boolean) => setMissing(flip(id, on));
  const allSelected = unticked.length === 0;
  const upToDate = preview !== null && preview.changes.length === 0 && preview.pantryCheck.length === 0;

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.applyMealPlanShopping(week, applyRequest(preview?.changes ?? [], unticked, missing, restored));
      notify('Lista atualizada com a ementa.', { severity: 'success' });
      navigate('/casa/compras');
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
      setBusy(false);
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <IconButton aria-label="Voltar" onClick={() => navigate('/casa/compras')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>Da ementa</Typography>
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="center" sx={{ mb: 1 }}>
        <IconButton aria-label="Semana anterior" onClick={() => moveWeek(-7)}><ChevronLeftIcon /></IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, minWidth: 140, textAlign: 'center' }}>{weekTitle(week)}</Typography>
        <IconButton aria-label="Semana seguinte" onClick={() => moveWeek(7)}><ChevronRightIcon /></IconButton>
      </Stack>
      {busy && <LinearProgress sx={{ mb: 1 }} />}
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {preview && (
        <Box sx={{ maxWidth: 640 }}>
          {upToDate && (
            <Typography color="text.secondary" sx={{ textAlign: 'center', my: 3 }}>
              A lista já tem tudo o que a ementa desta semana pede.
            </Typography>
          )}

          {preview.changes.length > 0 && (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <SectionTitle>Alterações à lista</SectionTitle>
                <Button size="small" onClick={() => setUnticked(allSelected ? preview.changes.map(c => c.catalogItemPublicId) : [])}>
                  {allSelected ? 'Desmarcar todos' : 'Marcar todos'}
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Desmarca o que não queres: o que já tens em casa fica fora da lista desta semana, e o que a ementa mudaria fica como está.
              </Typography>
              <Paper variant="outlined">
                <List dense disablePadding>
                  {preview.changes.map(c => {
                    const key = `${c.action}-${c.catalogItemPublicId}`;
                    const text = <ListItemText primary={changeText(c)} secondary={(c.note ? [c.note, ...c.meals] : c.meals).join(', ') || undefined} />;
                    const checked = !unticked.includes(c.catalogItemPublicId);
                    return (
                      <ListItem key={key} divider disablePadding>
                        <ListItemButton dense onClick={() => setUnticked(flip(c.catalogItemPublicId, checked))}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Checkbox
                              edge="start"
                              size="small"
                              checked={checked}
                              tabIndex={-1}
                              disableRipple
                              slotProps={{ input: { 'aria-label': c.action === 'Add' ? `Pôr ${c.name} na lista` : changeText(c) } }}
                            />
                          </ListItemIcon>
                          {text}
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            </>
          )}

          {preview.pantryCheck.length > 0 && (
            <>
              <SectionTitle>Confirma se tens</SectionTitle>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Básicos de despensa que a ementa usa. Marca os que faltam para irem para a lista.
              </Typography>
              <Paper variant="outlined">
                <List dense disablePadding>
                  {preview.pantryCheck.map(i => (
                    <ListItem
                      key={i.catalogItemPublicId}
                      divider
                      secondaryAction={
                        <Stack direction="row" alignItems="center">
                          <Typography variant="caption" color="text.secondary">Falta</Typography>
                          <Switch
                            edge="end"
                            checked={missing.includes(i.catalogItemPublicId)}
                            onChange={e => toggle(i.catalogItemPublicId, e.target.checked)}
                            slotProps={{ input: { 'aria-label': `Falta ${i.name}` } }}
                          />
                        </Stack>
                      }
                      sx={{ pr: 12 }}
                    >
                      <ListItemText
                        primary={`${i.name} ${amount(i.quantity, i.unit, i.hasUnquantified)}`.trim()}
                        secondary={i.meals.join(', ') || undefined}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </>
          )}

          {preview.inStock.length > 0 && (
            <>
              <SectionTitle>Já tens em casa</SectionTitle>
              {preview.inStock.map(i => (
                <Typography key={i.catalogItemPublicId} variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
                  {i.name} · {amount(i.quantity, i.unit, i.hasUnquantified)} (chega o que tens)
                </Typography>
              ))}
            </>
          )}

          {preview.leftOut.length > 0 && (
            <>
              <SectionTitle>Deixaste de fora</SectionTitle>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Disseste que tinhas em casa. Marca os que afinal queres comprar.
              </Typography>
              <Paper variant="outlined">
                <List dense disablePadding>
                  {preview.leftOut.map(i => (
                    <ListItem
                      key={i.catalogItemPublicId}
                      divider
                      secondaryAction={
                        <Stack direction="row" alignItems="center">
                          <Typography variant="caption" color="text.secondary">Comprar</Typography>
                          <Switch
                            edge="end"
                            checked={restored.includes(i.catalogItemPublicId)}
                            onChange={e => setRestored(flip(i.catalogItemPublicId, e.target.checked))}
                            slotProps={{ input: { 'aria-label': `Comprar ${i.name}` } }}
                          />
                        </Stack>
                      }
                      sx={{ pr: 14 }}
                    >
                      <ListItemText
                        primary={`${i.name} ${amount(i.quantity, i.unit, i.hasUnquantified)}`.trim()}
                        secondary={i.meals.join(', ') || undefined}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </>
          )}

          <Button variant="contained" size="large" fullWidth sx={{ mt: 3 }} onClick={apply}
            disabled={busy || (upToDate && preview.leftOut.length === 0)}>
            Atualizar a lista
          </Button>
        </Box>
      )}
    </>
  );
};

export default MealPlanShoppingPage;
